import { Overlay, OverlayConfig, OverlayRef } from "@angular/cdk/overlay";
import { CdkPortal } from "@angular/cdk/portal";
import { AfterViewInit, Component, ElementRef, forwardRef, Input, OnDestroy, ViewChild } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, NG_VALUE_ACCESSOR } from "@angular/forms";
import concat from "lodash/concat";
import { AggregationGroupModel } from "../../../models/search/aggregations/aggregation-group.model";
import { AggregationItemModel } from "../../../models/search/aggregations/aggregation-item.model";
import { FormControlBaseComponent } from "../form-control-base/form-control-base.component";
import { FacetOverlayMonitorService } from "./facet-overlay-monitor.service";

@Component({
    selector: "pcg-ng-facet",
    template: require('./facet.component.html'),
    providers: [
        {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => FacetComponent), multi: true},
    ]
})
export class FacetComponent extends FormControlBaseComponent<Array<string>> implements AfterViewInit, OnDestroy {
    @ViewChild("allOptions", { read: CdkPortal })
    private allOptions: CdkPortal;

    private overlayRef: OverlayRef;

    public PREVIEW_ITEMS_COUNT: number = 5;

    public formArray: FormArray;

    @Input()
    public aggregation: AggregationGroupModel;

    public constructor(
        private overlayMonitorService: FacetOverlayMonitorService,
        private domElement: ElementRef,
        private overlay: Overlay,
        private formBuilder: FormBuilder
    ) {
        super(/*DEFAULT_VALUE*/[]);
        this.formArray = this.formBuilder.array([]);
    }

    public writeValue(value: any): void {
        super.writeValue(value);
        this.buildControlsByAggregation(this.aggregation);
    }

    public getPreviewValues(): Array<AggregationItemModel> {
        const previewByCount = this.aggregation.aggregationItems
            .slice(0, this.PREVIEW_ITEMS_COUNT);

        const previewBySelection = this.aggregation.aggregationItems
                .filter(x => this.controlValue.indexOf(x.value) > -1)
                .filter(x => previewByCount.indexOf(x) < 0);

        const previewElements = concat(previewByCount, previewBySelection);

        return previewElements;
    }

    public getToggleForValue(value: string): FormGroup {
        const toggle = this.formArray.controls
            .find((x: FormGroup) => x.controls.key.value === value) as FormGroup;

        return toggle;
    }

    private buildControlsByAggregation(aggregation: AggregationGroupModel): void {
        this.formArray.controls.forEach((control, index) =>
            this.formArray.removeAt(index)
        );
        aggregation.aggregationItems.map(item => {
                const groupFormControl = this.formBuilder.group({
                    isSelected: [this.controlValue.indexOf(item.value) > -1],
                    key: [item.value]
                });

                groupFormControl.valueChanges.subscribe((value: { isSelected: boolean, key: string }) => {
                    const newValue = this.controlValue.slice();
                    if (value.isSelected) {
                        newValue.push(value.key);
                    } else {
                        newValue.splice(newValue.indexOf(value.key), 1);
                    }
                    this.writeNewValue(newValue);
                });

                return groupFormControl;
            }
        ).forEach(control => this.formArray.push(control));
    }

    public ngAfterViewInit(): void {
        const activeOverlay = this.overlayMonitorService.getActiveOverlay();

        if (activeOverlay === this.aggregation.alias) {
            this.showAllOptions();
        }
    }

    public showAllOptions(): void {
        const config: OverlayConfig = {
            positionStrategy: this.overlay.position()
                .flexibleConnectedTo(this.domElement)
                .withPositions([
                    {
                        originX: "end",
                        originY: "top",
                        overlayX: "start",
                        overlayY: "top"
                    },
                    {
                        originX: "end",
                        originY: "center",
                        overlayX: "start",
                        overlayY: "center"
                    },
                    {
                        originX: "end",
                        originY: "bottom",
                        overlayX: "start",
                        overlayY: "bottom"
                    }
                ])
                .withLockedPosition()
                .withFlexibleDimensions(false),
            maxWidth: 585,
            maxHeight: 550,
            hasBackdrop: true,
            backdropClass: "pcg-main-filter__overlay",
            scrollStrategy: this.overlay.scrollStrategies.reposition()
        };

        this.overlayRef = this.overlay.create(config);
        this.overlayRef.backdropClick().subscribe(() => this.hideAllOptions());
        this.overlayRef.attach(this.allOptions);

        this.overlayMonitorService.setActiveOverlay(this.aggregation.alias);
    }

    private hideAllOptions(): void {
        this.overlayRef.detach();
        this.overlayMonitorService.removeActiveOverlay();
    }

    public ngOnDestroy(): void {
        if (this.overlayRef && this.overlayRef.hasAttached()) {
            this.overlayRef.detach();
            this.overlayRef.dispose();
        }
    }
}
